import { AccessorFn, ColumnDef, DeepKeys } from "@tanstack/react-table";
import { TableCell } from "@/components/ui/table";
import { formatAdaptive } from "@/utils/number";
import { CellHoverClassName } from "@/components/List/ListTable/TableBody/Rows/TableDataRow";
import { format as d3format } from "d3-format";
import { CSSProperties } from "react";

type NumberColumnOptions<T> = ColumnDef<T> & {
	precision?: number;
	align?: "left" | "center" | "right";
	className?: string;
	numberclassName?: string;
	d3Format?: string | ((value: number) => string);
	accessorKey: DeepKeys<T> | AccessorFn<T>;
	style?: CSSProperties | ((value: number | null) => CSSProperties | undefined);
};

export const NumberColumn = <T,>({
	accessorKey,
	header,
	precision = 0,
	align = "left",
	className = "",
	numberclassName = "",
	d3Format,
	meta,
	style,
	...rest
}: NumberColumnOptions<T>): ColumnDef<T> => {
	const exportFormat = (value: unknown) => {
		if (value === null || value === undefined) return "-";

		const n = value as number;

		if (typeof d3Format === "function") {
			return d3Format(n);
		} else if (typeof d3Format === "string") {
			return d3format(d3Format)(n);
		}
		return formatAdaptive(n);
	};

	const mergedClassName = meta?.cellClassName || className;

	return {
		accessorKey: accessorKey as string,
		header,
		meta: { wrapWithTableCell: false, exportFormat, cellClassName: mergedClassName, ...meta },
		cell: ({ getValue }) => {
			const raw = getValue();
			const value = typeof raw === "number" ? raw : null;

			const formatted =
				value === null
					? "-"
					: typeof d3Format === "function"
						? d3Format(value)
						: typeof d3Format === "string"
							? d3format(d3Format)(value)
							: formatAdaptive(value);

			const alignment = {
				left: "text-left",
				center: "text-center",
				right: "text-right",
			}[align];

			const resolvedStyle = typeof style === "function" ? style(value) : style;
			return (
				<TableCell className={`${CellHoverClassName} ${alignment} ${mergedClassName}`} style={resolvedStyle}>
					<div className={`p-2 ${numberclassName}`}>{formatted}</div>
				</TableCell>
			);
		},
		...rest,
	};
};
